import { z } from "zod";

// Validation functions
export const validateEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const validateUsername = (username: string): boolean => {
  return /^[a-zA-Z0-9_.-]+$/.test(username) && username.length >= 3;
};

export const validateEmailOrUsername = (value: string): boolean => {
  return validateEmail(value) || validateUsername(value);
};

export const validatePhone = (phone: string): boolean => {
  return /^[0-9]{10}$/.test(phone.replace(/\D/g, ""));
};

export const validatePassword = (password: string): boolean => {
  return password.length >= 6;
};
 
export const validatePasswordMatch = (password: string, confirmPassword: string): boolean => {
  return password === confirmPassword;
};

export const validateFullName = (name: string): boolean => {
  return name.trim().length > 0;
};

export const validateAddress = (address: string): boolean => {
  return address.trim().length > 0;
};

// Validation error messages
export const validationMessages = {
  email: {
    required: "Email or username is required",
    invalid: "Please enter a valid email or username",
  },
  password: {
    required: "Password is required",
    invalid: "Password must be at least 6 characters",
  },
  confirmPassword: {
    required: "Confirm password is required",
    mismatch: "Passwords do not match",
  },
  phone: {
    required: "Phone number is required",
    invalid: "Phone number must be 10 digits",
  },
  fullName: {
    required: "Full name is required",
  },
  address: {
    required: "Address is required",
  },
  childName: {
    required: "Child name is required",
  },
  childClass: {
    required: "Class is required",
  },
  childSchool: {
    required: "School is required",
  },
  childStudentId: {
    required: "Student ID is required",
  },
};

// Type for validation result
export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

// Generic validation function
export const validateForm = (
  data: Record<string, any>,
  validators: Record<string, (value: any) => boolean | ValidationResult>
): ValidationResult => {
  const errors: Record<string, string> = {};

  Object.keys(validators).forEach((field) => {
    const validator = validators[field];
    const value = data[field];
    
    if (typeof validator === "function") {
      const result = validator(value);
      if (result === false) {
        errors[field] = validationMessages[field as keyof typeof validationMessages]?.required || "Invalid value";
      } else if (typeof result === "object" && !result.isValid) {
        errors[field] = result.errors[field] || "Invalid value";
      }
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// ============ ZOD SCHEMAS ============

/**
 * Login form validation schema
 * Accepts both email and username formats
 */
export const loginSchema = z.object({
  credential: z
    .string()
    .min(1, "Email or username is required")
    .refine(
      (val) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const usernameRegex = /^[a-zA-Z0-9._-]{3,}$/;
        return emailRegex.test(val) || usernameRegex.test(val);
      },
      "Enter a valid email or username"
    ),
  password: z.string().min(1, "Password is required"),
});

/**
 * Child form validation schema
 */
export const childSchema = z.object({
  name: z.string().min(2, "Child name must be at least 2 characters").max(50, "Child name is too long"),
  class: z.string().min(1, "Class is required").max(10, "Class is too long"),
  school: z.string().min(1, "School is required"),
  studentId: z.string().min(1, "Student ID is required").max(50, "Student ID is too long"),
});

/**
 * Parent form validation schema
 */
export const parentSchema = z
  .object({
    fullName: z.string().min(2, "Full name must be at least 2 characters").max(100, "Full name is too long"),
    email: z.string().email("Please enter a valid email address"),
    phone: z
      .string()
      .min(10, "Phone number must be at least 10 digits")
      .max(15, "Phone number is too long")
      .regex(/^\d+$/, "Phone number must contain only digits"),
    address: z.string().min(10, "Address must be at least 10 characters").max(250, "Address is too long"),
    state: z.string(),
    city: z.string(),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number"),
    confirmPassword: z.string().min(8, "Password must be at least 8 characters"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

/**
 * Registration form validation schema
 * Combines parent and children schemas
 */
export const registerSchema = z.object({
  parent: parentSchema,
  children: z.array(childSchema).min(1, "At least one child is required"),
});

// ============ TYPE DEFINITIONS ============
export type LoginFormData = z.infer<typeof loginSchema>;
export type ParentFormData = z.infer<typeof parentSchema>;
export type ChildFormData = z.infer<typeof childSchema>;
export type RegisterFormData = z.infer<typeof registerSchema>;

// Validation utilities
interface ServiceErrorResult {
  [key: string]: string[] | undefined;
  non_field_errors?: string[];
}

export const getServiceError = (error: any): ServiceErrorResult | string => {
  const { data = {}, status, statusText } = error.response || {};

  if (status === 400 && data && typeof data === "object" && Object.keys(data as Record<string, any>).length > 0) {
    return data as ServiceErrorResult;
  }

  return statusText || "Something went wrong";
};

export const formatValidationError = (errors: ServiceErrorResult | string): string => {
  if (typeof errors === "string") {
    return errors;
  }

  const fieldErrors = Object.entries(errors)
    .filter(([_, value]) => value && Array.isArray(value))
    .map(([_, value]) => value?.[0])
    .filter(Boolean);

  if (fieldErrors.length > 0) {
    return fieldErrors[0]!;  // or fieldErrors[0] as string
  }

  return "Validation failed";
};

